// The themed fallback for an unexpected error in a screen (FR-041; US6-2; research R15). Rendered by
// the ErrorBoundary every layout exports. It reports the error (scrubbed, non-production only) and
// never shows the error's text, which may carry personal data.
import { type Href, router } from "expo-router";
import { useEffect } from "react";
import { View } from "react-native";

import { type AppError, isAppError } from "@/api/errors";
import { reportError } from "@/lib/reportError";
import { landingHref } from "@/session/access";
import { useLanding } from "@/session/hooks";
import { ThemeProvider } from "@/theme/ThemeProvider";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";

import { useScreenStyles } from "./screenStyles";
import { Reference } from "./StateView";

/** Expo Router's ErrorBoundary props. */
export type ErrorFallbackProps = { error: Error; retry: () => Promise<void> };

function appErrorOf(error: unknown): AppError | null {
  if (isAppError(error)) return error;
  if (error instanceof Error && isAppError(error.cause)) return error.cause;
  return null;
}

/** Inside the providers: Go to Home goes to the user's landing (resolveLanding). */
export function ErrorFallback(props: ErrorFallbackProps) {
  const home = landingHref(useLanding()) ?? "/";
  return <ErrorFallbackView {...props} home={home} />;
}

/** For the root layout, whose boundary renders outside every provider: brings its own theme. */
export function RootErrorFallback(props: ErrorFallbackProps) {
  return (
    <ThemeProvider>
      <ErrorFallbackView {...props} home="/" />
    </ThemeProvider>
  );
}

export function ErrorFallbackView({
  error,
  retry,
  home,
}: ErrorFallbackProps & { home: string }) {
  const styles = useScreenStyles();

  useEffect(() => {
    reportError(error);
  }, [error]);

  return (
    <View style={styles.screen} testID="error-fallback">
      <View style={styles.body} role="main">
        <Text variant="heading" accessibilityRole="header" aria-level={1}>
          Something went wrong
        </Text>
        <Text tone="muted" style={styles.message}>
          This screen hit a problem. Try again, or go back to Home.
        </Text>
        <PressableTarget onPress={() => void retry()}>
          Try again
        </PressableTarget>
        <PressableTarget onPress={() => router.replace(home as Href)}>
          Go to Home
        </PressableTarget>
        <Reference requestId={appErrorOf(error)?.requestId ?? null} />
      </View>
    </View>
  );
}
